import React, { useEffect, useState, useRef, useMemo } from 'react'
import classNames from 'classnames'
import Text from '@/stories/components/Common/NewText'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import { useActiveSection } from '@/hooks/stocks/useActiveSection'
import { logNewRelicEvent } from '@/root/utils/common/custom/newrelic-page-events'

type NavItem = {
  id: string
  label: string
}

interface ScrollAwareTopNavProps {
  navItems: NavItem[]
  isMobile?: boolean
}

const ScrollAwareTopNav: React.FC<ScrollAwareTopNavProps> = ({
  navItems,
  isMobile = false,
}) => {
  const [isNavOpen, setIsNavOpen] = useState(false)
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const ref = useRef<HTMLDivElement>(null)

  const [stickyMobileNavOffset, setStickyMobileNavOffset] = useState(0)

  useEffect(() => {
    if (isMobile && isNavOpen && ref.current) {
      setStickyMobileNavOffset(ref.current.offsetHeight)
    } else {
      setStickyMobileNavOffset(0)
    }
  }, [isMobile, isNavOpen])

  const { activeId, scrollToSection } = useActiveSection(
    navItems,
    200,
    stickyMobileNavOffset - 50, // Apply a slight negative offset to ensure littleextra scroll and avoid alignment inaccuracies
    !!isMobile,
  )

  useEffect(() => {
    if (!activeId || !!isMobile) return
    const btn = buttonRefs.current[activeId]
    if (btn) {
      btn.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      })
    }
  }, [activeId, isMobile])

  const selectedLabel = useMemo(
    () => navItems.find(item => item.id === activeId)?.label,
    [activeId, navItems],
  )

  return (
    <nav
      className="sticky top-[3.75rem] z-30 bg-white pt-1 shadow-sm"
      ref={ref}
    >
      <ul className="herolike-wrapper no-scrollbar flex max-h-64 flex-col overflow-x-auto whitespace-nowrap md:flex-row md:gap-x-10">
        {isMobile && !isNavOpen && (
          <li>
            <button
              className="flex w-full justify-between py-2 text-left text-brand-blue-azureRadiance"
              onClick={() => setIsNavOpen(prev => !prev)}
              aria-expanded={isNavOpen}
            >
              {selectedLabel && (
                <Text appearance="subtitle1">{selectedLabel}</Text>
              )}
              <ArrowDown className="h-6 w-6 text-brand-black" />
            </button>
          </li>
        )}

        {(!isMobile || isNavOpen) &&
          navItems.map(({ id, label }, index) => (
            <li key={id}>
              <button
                onClick={() => {
                  scrollToSection(id)
                  setIsNavOpen(false)
                  logNewRelicEvent('scroll_aware_nav_click', { navItemId: id })
                }}
                ref={el => (buttonRefs.current[id] = el)}
                className={classNames(
                  'flex w-full justify-between py-2 text-left transition-colors md:border-b-2',
                  {
                    'text-brand-blue-azureRadiance md:border-brand-blue-azureRadiance md:text-brand-black':
                      activeId === id,
                    'border-transparent md:text-brand-black': activeId !== id,
                  },
                )}
                aria-current={activeId === id ? 'page' : undefined}
                aria-controls={id}
              >
                <Text appearance="subtitle1">{label}</Text>

                {index === 0 && isMobile && (
                  <div
                    onClick={e => {
                      e.stopPropagation()
                      setIsNavOpen(false)
                    }}
                  >
                    <ArrowDown className="h-6 w-6 rotate-180 text-brand-black" />
                  </div>
                )}
              </button>
            </li>
          ))}
      </ul>
    </nav>
  )
}

export default ScrollAwareTopNav
